import { ModalWithForm } from '@/components/ats/modal/ModalWithForm';
import { Flex, useDisclosure } from '@chakra-ui/react';
import { Button } from '@kula-ai/components';
import React, { useState, useImperativeHandle, forwardRef } from 'react';

interface ReportFooterProps {
  onSave?: () => Promise<void> | void;
  onCancel?: () => void;
  isSaving?: boolean;
  hasOriginalTiles?: boolean;
}

interface ReportFooterRef {
  setHasUnsavedChanges: (hasChanges: boolean) => void;
}

export const ReportFooter = forwardRef<ReportFooterRef, ReportFooterProps>(
  ({ onSave, onCancel, isSaving = false, hasOriginalTiles = true }, ref) => {
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    const handleSetHasUnsavedChanges = (hasChanges: boolean) => {
      setHasUnsavedChanges(hasChanges);
    };

    const { isOpen, onOpen, onClose } = useDisclosure();

    useImperativeHandle(ref, () => ({
      setHasUnsavedChanges: handleSetHasUnsavedChanges,
    }));

    const handleCancel = () => {
      if (hasUnsavedChanges) {
        onOpen();
      } else {
        onCancel?.();
      }
    };

    return (
      <>
        <Flex
          h="58px"
          justifyContent="flex-end"
          p="12px 24px"
          borderTop="1px solid"
          borderColor="border-color"
        >
          <Flex alignItems="center" gap="6px">
            {hasOriginalTiles && (
              <Button
                variant="secondary"
                onClick={handleCancel}
                disabled={isSaving || !hasOriginalTiles}
              >
                Cancel
              </Button>
            )}
            <Button
              variant="primary"
              onClick={() => {
                if (!hasUnsavedChanges) {
                  return;
                }
                onSave?.();
              }}
              disabled={isSaving || !hasUnsavedChanges}
              isLoading={isSaving}
              loadingText="Saving..."
            >
              Save
            </Button>
          </Flex>
        </Flex>
        {isOpen && (
          <ModalWithForm
            withForm={false}
            isOpen={isOpen}
            onClose={onClose}
            headerText="Cancel unsaved changes"
            subHeaderText="This action is permanent and cannot be undone"
            primaryCtaText="Confirm"
            handleSubmit={() => {
              onClose();
              onCancel?.();
            }}
          />
        )}
      </>
    );
  }
);

ReportFooter.displayName = 'ReportFooter';
